import { useEffect, useMemo, useRef, useState } from "react";
import { singular, type CardKind, type EventCard, type FileView, type Thing } from "@buildlab/codeview";
import type { WatchedValue } from "@buildlab/preview";
import { Alert, Book, Clock, Frame, Hand, Key, Play } from "./icons";

// The code view's Cards tab (Cards artboard, decision 26): "Things in your game" with live
// values, then the program as event cards. The picked card opens into narrated code, with
// "Change this card…" and "Ask about this" for the helper.

export interface Watch {
  build: number;
  values: Record<string, WatchedValue>;
  runs: Record<string, number>;
}

export interface CardLabel {
  title: string;
  steps: string[];
  notes: { line: number; text: string }[];
}

const KIND_ICON: Record<CardKind, (p: { size?: number; stroke?: string; width?: number }) => JSX.Element> = {
  start: Play,
  click: Hand,
  key: Key,
  event: Hand,
  timer: Clock,
  delay: Clock,
  frame: Frame,
  recipe: Book,
};

const TAGS: Record<string, [string, string]> = {
  button: ["button", "buttons"],
  div: ["box", "boxes"],
  span: ["bit of text", "bits of text"],
  p: ["paragraph", "paragraphs"],
  img: ["picture", "pictures"],
  li: ["list item", "list items"],
  canvas: ["canvas", "canvases"],
  input: ["text box", "text boxes"],
  h1: ["heading", "headings"],
  h2: ["heading", "headings"],
};
const tagWord = (tag: string | undefined, n: number) => (TAGS[tag ?? ""] ?? ["thing", "things"])[n === 1 ? 0 : 1];

interface History {
  first: string;
  last: string;
  changes: number;
  streak: number;
}

/** Text as a person reads it: curly quotes, and a list typed one per line reads "Ana, Ben, Cal" (not "Ana\\nBen"). */
function quoted(text: string): string {
  const parts = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const s = parts.join(", ");
  return `“${s.length > 32 ? `${s.slice(0, 31)}…` : s}”`;
}

/** What a thing's chip says, besides its name. */
function describeValue(v: WatchedValue | undefined, never: boolean): { value?: string; note?: string } {
  if (!v) return { note: "…" };
  switch (v.t) {
    case "unset":
      return { note: "not made yet" };
    case "none":
      return { note: "nothing yet" };
    case "number":
    case "boolean":
      return { value: v.text };
    case "string":
      return v.text.trim() === "" ? { note: "empty" } : { value: quoted(v.text) };
    case "element":
      if (v.in) return { note: `${singular(v.in.list)} ${v.in.index + 1}` };
      if (!v.text) return { note: `the ${tagWord(v.tag, 1)} on the page` };
      return { note: `shows ${quoted(v.text)}${never ? " · never changed" : ""}` };
    case "list":
      return v.tag ? { note: `${v.count} ${tagWord(v.tag, v.count)}` } : { note: v.count === 0 ? "empty list" : `list of ${v.count}` };
    case "drawing":
      return { note: "draws on the canvas" };
    case "function":
      return { note: "a recipe" };
    case "object":
      return { value: v.text };
  }
}

const valueText = (v: WatchedValue | undefined) => (!v ? "" : "text" in v && v.text !== undefined ? v.text : "count" in v ? String(v.count) : v.t);

export function CodeCards({
  views,
  labels,
  watch,
  errors,
  disabled,
  picked,
  onPick,
  onChange,
  onAsk,
  onShowCode,
}: {
  views: FileView[];
  labels: Record<string, CardLabel>;
  watch: Watch | null;
  errors: string[];
  disabled: boolean;
  picked: string | null;
  onPick: (id: string | null) => void;
  onChange: (card: EventCard, title: string) => void;
  onAsk: (card: EventCard, title: string) => void;
  onShowCode: (card: EventCard) => void;
}) {
  const things = views.flatMap((v) => v.things).filter((t) => t.kind !== "function");
  const cards = views.flatMap((v) => v.cards);
  const [focusThing, setFocusThing] = useState<string | null>(null);

  // How often each thing changed while the program ran (sampled a few times a second).
  const history = useRef<Map<string, History>>(new Map());
  const historyBuild = useRef(-1);
  if (watch && watch.build !== historyBuild.current) {
    history.current = new Map();
    historyBuild.current = watch.build;
  }
  if (watch) {
    for (const [name, v] of Object.entries(watch.values)) {
      const text = valueText(v);
      const h = history.current.get(name);
      if (!h) history.current.set(name, { first: text, last: text, changes: 0, streak: 0 });
      else if (h.last !== text) Object.assign(h, { last: text, changes: h.changes + 1, streak: h.streak + 1 });
      else h.streak = 0;
    }
  }

  // Cards light up when their run count goes up.
  const lastRuns = useRef<Record<string, number>>({});
  const [lit, setLit] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!watch) return;
    const now = new Set<string>();
    for (const c of cards) if (c.traceKey && (watch.runs[c.traceKey] ?? 0) > (lastRuns.current[c.traceKey] ?? 0)) now.add(c.id);
    lastRuns.current = watch.runs;
    if (now.size) {
      setLit(now);
      const t = setTimeout(() => setLit(new Set()), 450);
      return () => clearTimeout(t);
    }
  }, [watch]);

  const changedByCards = (t: Thing) => cards.some((c) => c.kind !== "start" && c.file === t.file && c.changes.includes(t.name));
  const insight = useMemo(() => findInsight(things, watch, history.current, changedByCards), [watch, views]);

  const parseErrors = views.filter((v) => v.error);
  const indent = (c: EventCard) => Math.min(...c.lines.filter((l) => l.n && l.text.trim()).map((l) => /^\s*/.exec(l.text)![0].length));
  const errorsFor = (c: EventCard) =>
    errors.filter((e) => {
      const m = /^([\w.-]+\.js):(\d+)\b/.exec(e);
      return m && m[1] === c.file && Number(m[2]) >= c.startLine && Number(m[2]) <= c.endLine;
    });

  if (views.length === 0) return <p className="cards-empty">This project has no JavaScript yet. Ask the helper to make something happen, and its cards show up here.</p>;

  return (
    <div className="codecards">
      {parseErrors.map((v) => (
        <div key={v.file} className="insight bad" role="status">
          <Alert size={18} stroke="#B5452F" />
          <span>
            The cards can't read <b>{v.file}</b> right now: {v.error!.message} on line {v.error!.line}. Fix the typo, or ask the helper to.
          </span>
        </div>
      ))}

      {things.length > 0 && (
        <div className="things">
          <span className="eyebrow">Things in your game</span>
          <div className="thing-list">
            {things.map((t) => {
              const v = watch?.values[t.name];
              const h = history.current.get(t.name);
              const never = !changedByCards(t);
              const d = describeValue(v, never);
              const changed = (h?.changes ?? 0) > 0;
              return (
                <button
                  key={`${t.file}:${t.name}`}
                  type="button"
                  className={`thing${changed ? " changed" : ""}${focusThing === t.name ? " on" : ""}`}
                  aria-pressed={focusThing === t.name}
                  title={`Show the cards that change ${t.name}`}
                  onClick={() => setFocusThing(focusThing === t.name ? null : t.name)}
                >
                  <code>{t.name}</code>
                  {d.value !== undefined && <span className="tval">{d.value}</span>}
                  {d.note && <span className="tnote">{d.note}</span>}
                  {changed && <span className="tnote">{h!.streak >= 3 ? "changing all the time" : `changed ${h!.changes}×`}</span>}
                </button>
              );
            })}
          </div>
          {insight && (
            <div className="insight bad" role="status">
              <Alert size={18} stroke="#B5452F" />
              <span>
                <b>{insight.value}</b> is {insight.now}, but the screen still says {insight.shows}. No card changes <b>{insight.element}</b>.
              </span>
            </div>
          )}
        </div>
      )}

      <div className="card-list">
        <span className="eyebrow">What happens, and when</span>
        {cards.map((c) => {
          const label = labels[c.id];
          const title = label?.title || c.title;
          const steps = label?.steps.length ? label.steps : c.steps;
          const notes = label?.notes.length ? label.notes : c.notes;
          const Icon = KIND_ICON[c.kind];
          const runs = c.traceKey ? watch?.runs[c.traceKey] ?? 0 : null;
          const open = picked === c.id;
          const errs = errorsFor(c);
          const touches = focusThing && (c.changes.includes(focusThing) ? "changes" : c.uses.includes(focusThing) ? "uses" : null);
          return (
            <article
              key={c.id}
              className={`ecard k-${c.kind}${open ? " open" : ""}${lit.has(c.id) ? " lit" : ""}${errs.length ? " has-error" : ""}${focusThing && !touches ? " dim" : ""}`}
              data-card={c.id}
            >
              <button type="button" className="ehead" aria-expanded={open} onClick={() => onPick(open ? null : c.id)}>
                <Icon size={15} width={2.2} />
                <span className="etitle">{title}</span>
                {touches && (
                  <span className="etag">
                    {touches} {focusThing}
                  </span>
                )}
                {runs !== null && runs > 0 && <span className="eruns">ran {runs}×</span>}
                {views.length > 1 && <span className="efile">{c.file}</span>}
              </button>
              <div className="ebody">
                <ul className="esteps">
                  {steps.length === 0 && <li className="muted">nothing yet</li>}
                  {steps.map((s, i) => (
                    <li key={i}>
                      <Bold text={s} names={things.map((t) => t.name)} />
                    </li>
                  ))}
                </ul>
                {errs.map((e, i) => (
                  <p key={i} className="eerror">
                    <Alert size={15} stroke="#B5452F" />
                    <code>{e}</code>
                  </p>
                ))}
                {open && (
                  <>
                    <div className="narrated">
                      <pre aria-label="This card's code">
                        {c.lines.map((l, i) => (
                          <span key={i} className={l.n === 0 ? "gap" : notes.some((n) => n.line === i + 1) ? "noted" : ""}>
                            <span className="ln" aria-hidden="true">
                              {l.n || ""}
                            </span>
                            {l.n ? l.text.slice(Math.min(indent(c), /^\s*/.exec(l.text)![0].length)) || " " : l.text}
                            {"\n"}
                          </span>
                        ))}
                      </pre>
                      <ul className="notes" aria-label="What the code does">
                        {notes.length === 0 && <li className="muted">No notes for this card yet.</li>}
                        {notes.map((n, i) => (
                          <li key={i}>
                            <span className="nl">line {c.lines[n.line - 1]?.n || "?"}</span>
                            <Bold text={n.text} names={things.map((t) => t.name)} />
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="row">
                      <button type="button" className="primary small" disabled={disabled} onClick={() => onChange(c, title)}>
                        Change this card…
                      </button>
                      <button type="button" className="outline small" disabled={disabled} onClick={() => onAsk(c, title)}>
                        Ask about this
                      </button>
                      <button type="button" className="linklike muted-link" onClick={() => onShowCode(c)}>
                        Show all code
                      </button>
                    </div>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/** Bold the program's own names inside a caption, as the design does. */
function Bold({ text, names }: { text: string; names: string[] }) {
  if (names.length === 0) return <>{text}</>;
  const re = new RegExp(`\\b(${names.map((n) => n.replace(/[$]/g, "\\$")).join("|")})\\b`, "g");
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p))}
    </>
  );
}

/**
 * The planted-bug pattern from L4 (and a common one in student games): a number went up, the
 * element that showed it still shows the starting value, and no card ever changes that element.
 */
function findInsight(things: Thing[], watch: Watch | null, history: Map<string, History>, changedByCards: (t: Thing) => boolean) {
  if (!watch) return null;
  for (const el of things) {
    const ev = watch.values[el.name];
    if (ev?.t !== "element" || ev.in || changedByCards(el)) continue;
    for (const n of things) {
      const nv = watch.values[n.name];
      const h = history.get(n.name);
      if (nv?.t !== "number" || !h || h.changes === 0) continue;
      if (ev.text === h.first && nv.text !== ev.text) return { value: n.name, now: nv.text, shows: ev.text, element: el.name };
    }
  }
  return null;
}
